import React, { useContext, useMemo, useState } from "react";
import cx from "classnames";
import {
  Box,
  Heading,
  Text,
  ThemeStore,
  Checkbox,
  Pill,
  Loading,
  Button,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ContextEDH } from "@context/ContextEDH";
import { CampusCategory } from "@context/base.reducer";
import { Campus } from "@models/ISchema";
import { AdaptedGroup } from "../../models";
import { IParams } from "@models/IParams";
import { useCreateSubgroups } from "../../hooks/useCreateSubgroup";
import { Icon } from "@foris/avocado-icons";
import css from "./subgroupsCreation.module.scss";

interface AdaptedGrouper extends CampusCategory {
  campuses: string[];
}

interface Props {
  parentGroup: AdaptedGroup;
  setDisplayCreation: (display: boolean) => void;
  subgroups: AdaptedGroup[];
  requestSubgroups: () => void;
  campuses: Campus[];
}

const getCampusGroupersPills = (groupers: AdaptedGrouper[] = [], campusCode = "") => {
  return (
    <div className={css.campusItem_groupers}>
      {groupers
        ?.filter(grouper => grouper.campuses.includes(campusCode))
        .map(grouper => (
          <Pill key={grouper.code} className={css.grouperLabel}>
            {grouper.name}
          </Pill>
        ))}
    </div>
  );
};

const getCampusIdsFromCampusCodes = (
  campuses: Campus[],
  campusCodes: Record<Campus["code"], boolean>,
) => {
  const idsRecord: Record<Campus["id"], boolean> = {};

  campuses?.forEach(campus => {
    if (campusCodes?.[campus?.code]) {
      idsRecord[campus?.id] = true;
    }
  });

  return idsRecord;
};

const SubgroupsCreation: React.FC<Props> = ({
  parentGroup,
  setDisplayCreation,
  subgroups,
  requestSubgroups,
  campuses,
}) => {
  const { t } = useTranslation();
  const { state } = useContext(ContextEDH);
  const { scenario, origin }: IParams = useParams();
  const [{ isLoading }, submitCreations] = useCreateSubgroups({ scenario, origin });
  const [selectedCampusCodes, setSelectedCampusCodes] = useState<Record<Campus["code"], boolean>>(
    {},
  );
  const [isGroupersOpen, setIsGroupersOpen] = useState(false);
  const [selectedGroupers, setSelectedGroupers] = useState<{
    [grouperCode: string]: CampusCategory & {
      campuses: string[];
    };
  }>({});
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);

  const groupers = state?.base?.campusCategoriesConfig?.campus_categories;

  const alreadyCreatedCampusesByCodes = useMemo(() => {
    const campusCodes = (subgroups ?? []).map(subgroup => subgroup?.campus?.code);
    return new Set(campusCodes);
  }, [subgroups]);

  const enabledCampuses = useMemo(() => {
    return campuses?.filter(campus => !alreadyCreatedCampusesByCodes.has(campus?.code));
  }, [campuses, alreadyCreatedCampusesByCodes]);

  const enabledCampusesCodes = useMemo(() => {
    return enabledCampuses?.map(campus => campus?.code);
  }, [enabledCampuses]);

  const isAllCampusesSelected = useMemo(() => {
    return enabledCampuses?.length === Object.keys(selectedCampusCodes).length;
  }, [selectedCampusCodes, enabledCampuses]);

  const toggle = (campusCode: Campus["code"]) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.checked) {
      setSelectedCampusCodes({ ...selectedCampusCodes, [campusCode]: true });
    } else {
      setSelectedCampusCodes(
        Object.fromEntries(
          Object.entries(selectedCampusCodes).filter(([code]) => code !== campusCode),
        ),
      );
    }
  };

  const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.checked) {
      setSelectedCampusCodes(
        enabledCampuses.reduce((acc, campus) => ({ ...acc, [campus?.code]: true }), {}),
      );
    } else {
      setSelectedCampusCodes({});
      setSelectedGroupers({});
    }
  };

  const handleToggleGrouper = (isGrouperSelected: boolean, grouper: CampusCategory) => {
    if (isGrouperSelected) {
      const campusesCodes = grouper?.campus.map(campus => campus?.code);

      setSelectedGroupers({
        ...selectedGroupers,
        [grouper?.code]: {
          ...grouper,
          campuses: campusesCodes,
        },
      });

      const campusesCodesToSelect = [...enabledCampusesCodes]?.filter(
        campusId => campusesCodes.includes(campusId) && !selectedCampusCodes[campusId],
      );

      setSelectedCampusCodes({
        ...selectedCampusCodes,
        ...campusesCodesToSelect.reduce(
          (acc, campuesCode) => ({ ...acc, [campuesCode]: true }),
          {},
        ),
      });
    } else {
      const newSelectedGroupers = { ...selectedGroupers };
      const grouperToDelete = newSelectedGroupers[grouper?.code];

      delete newSelectedGroupers[grouper?.code];

      const campusesCodesToUnselect = grouperToDelete?.campuses.filter(
        campusCode =>
          selectedCampusCodes?.[campusCode] &&
          Object.values(newSelectedGroupers).every(
            ({ campuses }) => !campuses.includes(campusCode),
          ),
      );

      const newSelectedCampusIds = { ...selectedCampusCodes };
      campusesCodesToUnselect.forEach(campusCode => {
        delete newSelectedCampusIds[campusCode];
      });

      setSelectedCampusCodes(newSelectedCampusIds);
      setSelectedGroupers(newSelectedGroupers);
    }
  };

  return (
    <section className={css.main}>
      {isLoading && <Loading />}

      <Box className={css.card}>
        <header className={css.card__header}>
          <Heading className={css.card__header__title} type="h3">
            {t("groups-manager.subgroups.creation.title")}
          </Heading>
          <Text className={css.card__header__subtitle} type="md">
            {t("groups-manager.subgroups.creation.description")}
          </Text>
        </header>

        {!!state?.base?.campusCategoriesConfig?.enabled && (
          <div
            className={cx(
              css.groupers,
              isGroupersOpen && css.groupers__open,
              themeMode === "dark" && css.groupers__dark,
            )}
          >
            <div className={css.groupers_header}>
              <button
                className={cx(css.groupersTrigger, isGroupersOpen && css.groupersTrigger__open)}
                onClick={() => setIsGroupersOpen(!isGroupersOpen)}
              >
                <Icon name="chevron-down" />
              </button>
              {t("groups-manager.subgroups.creation.groupers")}
            </div>

            <div className={css.groupers_content}>
              <Checkbox
                checked={isAllCampusesSelected}
                labelRight={t("groups-manager.subgroups.creation.all-groupers")}
                disabled={isLoading}
                onChange={handleToggleAll}
              />

              <div className={css.groupersList}>
                {groupers?.map(grouper => (
                  <Checkbox
                    key={grouper?.code}
                    className={css.groupersList_checkbox}
                    labelRight={grouper?.name}
                    disabled={isLoading}
                    checked={!!selectedGroupers?.[grouper?.code]}
                    onChange={event => handleToggleGrouper(event.target.checked, grouper)}
                  />
                ))}
              </div>

              <div className={css.groupersFooter}>
                <Button
                  className={css.groupersFooter_action}
                  variant="ghost"
                  onClick={() => {
                    setSelectedCampusCodes({});
                    setSelectedGroupers({});
                  }}
                >
                  {t("groups-manager.subgroups.creation.unmark-selection")}
                </Button>
              </div>
            </div>
          </div>
        )}

        <ul className={css.card_campuses}>
          {campuses?.map(campus => (
            <li key={campus?.code} className={css.campusItem}>
              <Checkbox
                className={css.campusItem_checkbox}
                onChange={toggle(campus?.code)}
                checked={
                  alreadyCreatedCampusesByCodes?.has(campus?.code) ||
                  campus?.code in selectedCampusCodes
                }
                disabled={alreadyCreatedCampusesByCodes.has(campus?.code) || isLoading}
                labelRight={`${campus?.code} ${campus?.name}`}
              />

              {getCampusGroupersPills(Object.values(selectedGroupers), campus?.code)}
            </li>
          ))}
        </ul>
        <div className={css.card__bottom}>
          <div className={css.bottomCounts}>
            <div className={css.bottomCounts_label}>
              <span className={css.labelCount}>{alreadyCreatedCampusesByCodes?.size ?? 0}</span>{" "}
              {t("groups-manager.subgroups.creation.saved-subgroups")}
            </div>

            <div className={css.bottomCounts_label}>
              <span className={css.labelCount}>
                {Object.keys(selectedCampusCodes)?.length ?? 0}
              </span>{" "}
              {t("groups-manager.subgroups.creation.selected-campuses")}
            </div>
          </div>
          <div className={css.card__bottom__buttons}>
            <Button disabled={isLoading} variant="ghost" onClick={() => setDisplayCreation(false)}>
              {t("groups-manager.subgroups.creation.cancel")}
            </Button>
            <Button
              disabled={Object.keys(selectedCampusCodes).length === 0 || isLoading}
              onClick={async () => {
                await submitCreations(
                  parentGroup,
                  getCampusIdsFromCampusCodes(campuses, selectedCampusCodes),
                );
                requestSubgroups();
                setDisplayCreation(false);
              }}
            >
              {t("groups-manager.subgroups.creation.save-changes")}
            </Button>
          </div>
        </div>
      </Box>
    </section>
  );
};

export default SubgroupsCreation;
